import { parseNotifications } from '../../notifications';
import type { AlertRowAction } from './actions';
import type { AlertRowState, ConditionOperation } from './types';

/**
 * Check if a condition has an empty value (no min or max set).
 * Used for auto-enable logic.
 */
export function hasEmptyConditionValue(condition: AlertRowState['condition']): boolean {
    if (condition?.type !== 'quantitative') return false;
    return condition.min === null && condition.max === null;
}

/**
 * Get the numeric value from a condition.
 */
export function getConditionValue(condition: AlertRowState['condition']): number | undefined {
    if (condition?.type !== 'quantitative') return undefined;
    if (condition.max !== null) return condition.max.value;
    if (condition.min !== null) return condition.min.value;
    return undefined;
}

/**
 * Get the operation (greaterThan/lessThan) from a condition.
 */
export function getOperationFromCondition(condition: AlertRowState['condition']): ConditionOperation {
    if (condition?.type === 'quantitative') {
        if (condition.max !== null && condition.min === null) return 'lessThan';
    }
    return 'greaterThan';
}

/**
 * Update a quantitative condition with a new value and operation.
 * Returns the updated condition or null if the input is not quantitative.
 */
function updateConditionValue(
    condition: AlertRowState['condition'],
    value: number | undefined,
    operation: ConditionOperation,
): AlertRowState['condition'] {
    if (condition?.type !== 'quantitative') return condition;

    // Handle undefined/empty value - set both min and max to null
    if (value === undefined) {
        return {
            ...condition,
            min: null,
            max: null,
        };
    }

    // Apply value based on operation
    return {
        ...condition,
        min: operation === 'greaterThan' ? { value, mode: 'exclusive' } : null,
        max: operation === 'lessThan' ? { value, mode: 'exclusive' } : null,
    };
}

/**
 * Update a quantitative condition with a new indicator.
 */
function updateConditionIndicator(
    condition: AlertRowState['condition'],
    indicatorId: string,
): AlertRowState['condition'] {
    if (condition?.type !== 'quantitative') return condition;

    return {
        ...condition,
        indicator: { id: indicatorId },
    };
}

/**
 * Pure reducer function for alert row state.
 * All business logic (like auto-enable rules) is centralized here.
 */
export function alertRowReducer(state: AlertRowState, action: AlertRowAction): AlertRowState {
    switch (action.type) {
        case 'SET_CONDITION': {
            const newOperation = getOperationFromCondition(action.condition);
            return {
                ...state,
                condition: action.condition,
                // Update localOperation to match the condition's operation
                localOperation: newOperation,
            };
        }

        case 'SET_ENABLED': {
            return {
                ...state,
                enabled: action.enabled,
            };
        }

        case 'SET_SHOW_TOAST': {
            return {
                ...state,
                showToast: action.showToast,
            };
        }

        case 'SET_PLAY_SOUND': {
            return {
                ...state,
                playSound: action.playSound,
            };
        }

        case 'SET_SOUND_TYPE': {
            return {
                ...state,
                soundType: action.soundType,
            };
        }

        case 'SET_SPEAK_TTS': {
            return { ...state, speakTts: action.speakTts };
        }

        case 'SET_TTS_TEXT': {
            return { ...state, ttsText: action.ttsText };
        }

        case 'SET_TTS_VOICE_URI': {
            return { ...state, ttsVoiceUri: action.ttsVoiceUri };
        }

        case 'SET_TTS_RATE': {
            return { ...state, ttsRate: action.ttsRate };
        }

        case 'SET_TTS_PITCH': {
            return { ...state, ttsPitch: action.ttsPitch };
        }

        case 'SET_TTS_VOLUME': {
            return { ...state, ttsVolume: action.ttsVolume };
        }

        case 'SET_NOTE': {
            return {
                ...state,
                note: action.note,
            };
        }

        case 'SET_CONDITION_VALUE': {
            const newCondition = updateConditionValue(state.condition, action.value, state.localOperation);

            /**
             * AUTO-ENABLE RULE:
             * When a disabled alert has an empty condition value, providing a value should enable the alert.
             * This helps users quickly create alerts without needing to manually toggle the enable switch.
             *
             * The rule applies when:
             * 1. The value was empty when the input received focus (wasEmptyOnFocus)
             * 2. A value has now been provided (value !== undefined)
             * 3. The alert is currently disabled (!state.enabled)
             */
            const shouldAutoEnable = action.wasEmptyOnFocus && action.value !== undefined && !state.enabled;

            return {
                ...state,
                condition: newCondition,
                enabled: shouldAutoEnable ? true : state.enabled,
            };
        }

        case 'SET_CONDITION_VALUE_FROM_INDICATOR': {
            const wasEmpty = hasEmptyConditionValue(state.condition);

            // Round value based on indicator unit:
            // - Percentages are 0-1 scale, need 4 decimals to get 2 after *100
            // - Other values (prices, etc.) need 2 decimals
            const scale = action.isPercent ? 10000 : 100;
            const roundedValue = Math.round(action.value * scale) / scale;

            // Preserve the current operation when clicking indicator value
            const newCondition = updateConditionValue(state.condition, roundedValue, state.localOperation);

            /**
             * AUTO-ENABLE RULE:
             * Same rule as SET_CONDITION_VALUE - when clicking the indicator value
             * on a disabled alert with an empty condition, enable it.
             */
            const shouldAutoEnable = wasEmpty && !state.enabled;

            return {
                ...state,
                condition: newCondition,
                enabled: shouldAutoEnable ? true : state.enabled,
            };
        }

        case 'SET_CONDITION_OPERATION': {
            const currentValue = getConditionValue(state.condition);

            // If there's a value, update the condition with the new operation
            const newCondition =
                currentValue !== undefined
                    ? updateConditionValue(state.condition, currentValue, action.operation)
                    : state.condition;

            // Always update localOperation so it's remembered for when a value is entered
            return {
                ...state,
                condition: newCondition,
                localOperation: action.operation,
            };
        }

        case 'SET_CONDITION_INDICATOR': {
            const newCondition = updateConditionIndicator(state.condition, action.indicatorId);

            return {
                ...state,
                condition: newCondition,
            };
        }

        case 'SYNC_FROM_DEFINITION': {
            const { showToast, sound, speakTts, ttsText, ttsVoiceUri, ttsRate, ttsPitch, ttsVolume } =
                parseNotifications(action.definition.notifications);

            // When syncing from server, derive localOperation from the condition.
            // However, if the condition is empty (no value), preserve the current localOperation
            // so the user's intended direction isn't lost when server responds.
            const incomingOperation = getOperationFromCondition(action.definition.condition);
            const conditionIsEmpty = hasEmptyConditionValue(action.definition.condition);
            const localOperation = conditionIsEmpty ? state.localOperation : incomingOperation;

            return {
                ...state,
                condition: action.definition.condition,
                enabled: action.definition.enabled,
                showToast,
                playSound: sound !== '',
                soundType: sound || 'tambourine',
                speakTts,
                ttsText,
                ttsVoiceUri,
                ttsRate,
                ttsPitch,
                ttsVolume,
                note: action.definition.userNote ?? null,
                localOperation,
            };
        }

        default: {
            // Exhaustiveness check - TypeScript will error if a case is missing
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
